import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Common";
import { AllegatiTab } from "@/components/AllegatiPanel";
import { STATO_CORSO, STATO_ISCRIZIONE, fmtDate, fmtDateTime, fmtEuro } from "@/lib/constants";
import {
  PERSONA_FIELDS, DOCENTE_FIELDS, CATEGORIE_ALLEGATO, contrattoLabel, cobWarning, ruoliLabel,
} from "@/lib/anagrafiche";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, Loader2, Pencil, IdCard, History, Paperclip, AlertTriangle, GraduationCap, Clock,
} from "lucide-react";
import { toast } from "sonner";

const CFG = {
  persone: { fields: PERSONA_FIELDS, fk: "persona_id", label: "Partecipante" },
  docenti: { fields: DOCENTE_FIELDS, fk: "docente_id", label: "Docente" },
};

function fmtValue(f, v, extra) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (f.type === "date") return fmtDate(v);
  if (f.type === "tags") return v.join(", ");
  if (f.type === "checks") return ruoliLabel(v);
  if (f.name === "tipo_contratto") return contrattoLabel(v);
  if (f.name === "sesso") return v === "M" ? "Maschio" : v === "F" ? "Femmina" : v;
  if (f.type === "relation") return extra.azienda_nome || "—";
  if (f.type === "number" && /tariffa|importo/.test(f.name)) return fmtEuro(v);
  return String(v);
}

function AnagraficaCard({ fields, a, extra, canEcon }) {
  const groups = [];
  fields.filter((f) => !(f.econ && !canEcon)).forEach((f) => {
    if (f.type === "heading") groups.push({ title: f.label, items: [] });
    else if (groups.length && (!f.showIf || f.showIf(a))) groups[groups.length - 1].items.push(f);
  });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {groups.map((g) => (
        <div key={g.title} className="rounded-xl border bg-white p-5" data-testid={`group-${g.title}`}>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-[#E11D48]">{g.title}</h3>
          <dl className="mt-3 space-y-2 text-sm">
            {g.items.map((f) => (
              <div key={f.name} className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">{f.label}</dt>
                <dd className="font-medium text-right text-slate-800" data-testid={`val-${f.name}`}>{fmtValue(f, a[f.name], extra)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

function StoricoTab({ kind, data, canEcon }) {
  const navigate = useNavigate();
  const isP = kind === "persone";
  const heads = isP ? ["Codice", "Corso", "Stato corso", "Stato iscrizione", "Ore freq.", "Azienda", "Periodo"]
    : ["Codice", "Corso", "Stato corso", "Ruolo", "Ore eff./prev.", "Sessioni", ...(canEcon ? ["Compenso"] : []), "Periodo"];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-white p-3"><div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" />Corsi</div><div className="mt-1 font-display text-xl font-bold" data-testid="tot-corsi">{data.totali.n_corsi}</div></div>
        <div className="rounded-lg border bg-white p-3"><div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5"><Clock className="h-4 w-4" />Ore totali</div><div className="mt-1 font-display text-xl font-bold" data-testid="tot-ore">{data.totali.ore}h</div></div>
      </div>
      {data.corsi.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-white py-14 text-center text-sm text-slate-400">Nessuna attività con INDAR</div>
      ) : (
        <div className="rounded-xl border bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">{heads.map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.corsi.map((r) => (
                <TableRow key={r.link_id} className="cursor-pointer" onClick={() => navigate(`/corsi/${r.corso_id}`)} data-testid="storico-row">
                  <TableCell className="font-mono text-xs font-semibold text-[#E11D48]">{r.codice}</TableCell>
                  <TableCell className="font-medium">{r.titolo}</TableCell>
                  <TableCell><Badge label={STATO_CORSO[r.stato_corso]?.label || r.stato_corso} cls={STATO_CORSO[r.stato_corso]?.cls || ""} /></TableCell>
                  {isP ? <>
                    <TableCell className="text-sm">{STATO_ISCRIZIONE[r.stato] || r.stato}</TableCell>
                    <TableCell className="text-sm">{r.ore}h</TableCell>
                    <TableCell className="text-sm">{r.azienda_nome || "—"}</TableCell>
                  </> : <>
                    <TableCell className="text-sm capitalize">{r.ruolo}</TableCell>
                    <TableCell className="text-sm">{r.ore}h / {r.ore_previste}h</TableCell>
                    <TableCell className="text-sm">{r.n_sessioni}</TableCell>
                    {canEcon && <TableCell className="text-sm font-semibold">{fmtEuro(r.costo)}</TableCell>}
                  </>}
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.data_avvio)} → {fmtDate(r.data_termine)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {data.eventi.length > 0 && (
        <div className="rounded-xl border bg-white p-5">
          <h3 className="font-display text-sm font-semibold text-slate-800">Registro modifiche</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {data.eventi.slice(0, 20).map((e) => (
              <li key={e.id} className="flex justify-between gap-3 border-b border-slate-100 pb-2" data-testid="evento-row">
                <span className="text-slate-700">{e.action} · {e.entity}{e.detail ? ` — ${e.detail}` : ""}</span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{e.user_name || e.user_email} · {fmtDateTime(e.timestamp)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AnagraficaDetail({ kind }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/anagrafica/${kind}/${id}/storico`);
      setData(data);
    } catch {
      toast.error("Anagrafica non trovata");
      navigate(`/${kind}`);
    }
  }, [kind, id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const a = data.anagrafica;
  const warn = kind === "docenti" ? cobWarning(a) : "";

  return (
    <div data-testid="anagrafica-detail-page">
      <Link to={`/${kind}`} className="mb-4 flex min-h-9 w-fit items-center gap-1.5 py-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Torna a {kind === "persone" ? "Persone" : "Personale (docenti e tutor)"}
      </Link>
      <div className="rounded-xl border bg-white p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-[#E11D48]">{kind === "docenti" ? ruoliLabel(a.ruoli) : CFG[kind].label}</span>
          <h1 className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="anagrafica-nome">{a.cognome} {a.nome}</h1>
          <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
            <span className="font-mono">{a.cf || "—"}</span>
            {data.azienda_nome && <span>{data.azienda_nome}</span>}
            {a.email && <span>{a.email}</span>}
          </div>
        </div>
        {user?.can_write && (
          <Button variant="outline" size="sm" onClick={() => navigate(`/${kind}?edit=${id}`)} data-testid="edit-anagrafica-btn">
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
          </Button>
        )}
      </div>
      {warn && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="cob-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {warn}
        </div>
      )}
      <Tabs defaultValue="anagrafica" className="mt-6">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica"><IdCard className="mr-1.5 h-4 w-4" />Anagrafica</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico"><History className="mr-1.5 h-4 w-4" />Storico INDAR</TabsTrigger>
          <TabsTrigger value="allegati" data-testid="tab-allegati"><Paperclip className="mr-1.5 h-4 w-4" />Allegati ({data.documenti.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="anagrafica" className="mt-4">
          <AnagraficaCard fields={CFG[kind].fields} a={a} extra={data} canEcon={user?.can_economics} />
        </TabsContent>
        <TabsContent value="storico" className="mt-4"><StoricoTab kind={kind} data={data} canEcon={user?.can_economics} /></TabsContent>
        <TabsContent value="allegati" className="mt-4"><AllegatiTab docs={data.documenti} canWrite={user?.can_write} reload={load} link={{ [CFG[kind].fk]: id }} categorie={CATEGORIE_ALLEGATO} /></TabsContent>
      </Tabs>
    </div>
  );
}
